import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HelloSign from 'hellosign-embedded';
import {
  Phone, Loader2, FileText, Scale, Check, X, Users, User, HelpCircle,
  Activity, Bone, AlertTriangle, Car, MoreHorizontal, ChevronLeft, CalendarDays, MapPin,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PHONE_DISPLAY = '(864) 365-3248';
const PHONE_LINK = 'tel:+18643653248';
const LOGO = 'https://customer-assets.emergentagent.com/job_rizzi-practice/artifacts/j5bpvf84_logo.png';

// Toggle to re-enable the embedded Dropbox Sign document step. Set to true to bring it back.
const ENABLE_ESIGN = false;

const sendToLeadWebhook = (data) => {
  try {
    fetch(`${API_URL}/api/forward-lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      keepalive: true,
    }).catch(() => {});
  } catch { /* fire-and-forget */ }
};

const FORM_CONSENT = 'By submitting this form, you agree to be contacted by phone, text, or email about your accident. Message and data rates may apply. Consent is not required as a condition of services. Submitting this form does not create an attorney-client relationship and does not guarantee case acceptance or compensation.';

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const CityAutocomplete = ({ value, onChange, idPrefix = '' }) => {
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);

  useEffect(() => {
    let active = true;
    import('../data/usCities.json').then((m) => { if (active) setCities(m.default || m); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const q = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (q.length < 2 || cities.length === 0) return [];
    const rank = (c) => (c.endsWith(', SC') ? 0 : c.endsWith(', NC') ? 1 : 2);
    const starts = [];
    const contains = [];
    for (let i = 0; i < cities.length; i++) {
      const lc = cities[i].toLowerCase();
      if (lc.startsWith(q)) { if (starts.length < 60) starts.push(cities[i]); }
      else if (contains.length < 20 && lc.includes(q)) contains.push(cities[i]);
    }
    starts.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
    return starts.concat(contains).slice(0, 8);
  }, [q, cities]);

  const select = (city) => { onChange(city); setOpen(false); setHighlight(-1); };

  const onKeyDown = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0) { e.preventDefault(); select(matches[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapRef}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
        <input
          type="text" value={value} autoComplete="off" autoFocus
          onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Start typing your city..."
          className="block w-full max-w-full min-w-0 box-border pl-11 pr-4 py-3 rounded-lg border border-slate-300 text-[15px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          data-testid={`${idPrefix}input-location`}
        />
      </div>
      {open && matches.length > 0 && (
        <ul className="mt-1 bg-white border border-slate-200 rounded-lg shadow-sm max-h-60 overflow-auto" data-testid={`${idPrefix}city-options`}>
          {matches.map((city, i) => (
            <li key={city}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); select(city); }}
                className={`w-full flex items-center gap-2 px-4 py-2.5 text-left text-[15px] text-slate-700 ${i === highlight ? 'bg-primary/10' : 'hover:bg-slate-50'}`}
                data-testid={`${idPrefix}city-option-${i}`}>
                <MapPin className="h-4 w-4 text-slate-400 flex-shrink-0" /> {city}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const QUIZ_STEPS = [
  {
    key: 'injured', question: 'Were you injured?',
    options: [
      { label: 'Yes', icon: Check },
      { label: 'No', icon: X },
    ],
  },
  {
    key: 'fault', question: 'Who was at fault?',
    options: [
      { label: 'Them', icon: Users },
      { label: 'Me', icon: User },
      { label: "I don't know", icon: HelpCircle },
    ],
  },
  {
    key: 'injuries', question: 'Which of the following best describes your injuries?',
    options: [
      { label: 'Neck, back, knee, or shoulder injury', icon: Activity },
      { label: 'Broken bones', icon: Bone },
      { label: 'Severe injury/death', icon: AlertTriangle },
      { label: 'Other', icon: MoreHorizontal },
    ],
  },
  {
    key: 'how', question: 'How did the accident happen?',
    options: [
      { label: 'I was rear-ended', icon: Car },
      { label: 'The other party failed to yield', icon: AlertTriangle },
      { label: 'The other party violated the rules of the road', icon: Scale },
      { label: 'Other', icon: MoreHorizontal },
    ],
  },
];

const PROGRESS = [14, 29, 43, 57, 71, 95, 100];
const TOTAL_STEPS = 7;

const DATE_OPTIONS = [
  { label: 'In the past month', icon: CalendarDays },
  { label: 'In the past 3 years', icon: CalendarDays },
  { label: 'More than 3 years ago', icon: CalendarDays },
];

const MultiStepForm = ({ idPrefix = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};
  const hasContact = !!(prefill.firstName && prefill.phone && prefill.email);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    injured: '', fault: '', injuries: '', how: '',
    location: '', incidentDate: '',
    firstName: prefill.firstName || '', lastName: prefill.lastName || '',
    phone: prefill.phone || '', email: prefill.email || '',
  });
  const [consent, setConsent] = useState(hasContact);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [signData, setSignData] = useState(null);

  const openEmbedded = (url, clientId) => {
    const client = new HelloSign({ clientId });
    client.on('sign', () => { navigate('/car-accident/thank-you'); });
    client.on('error', () => {});
    client.open(url, { skipDomainVerification: true, testMode: true });
  };

  const set = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));
  const upd = (k) => (e) => { set(k, e.target.value); setError(''); };

  const goNext = () => { setError(''); setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1)); };
  const goBack = () => { setError(''); setStep((s) => Math.max(s - 1, 0)); };

  const chooseOption = (key, value) => {
    set(key, value);
    setError('');
    setTimeout(() => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1)), 120);
  };

  const chooseDate = (value) => {
    set('incidentDate', value);
    setError('');
    // If contact info was passed from the /car-accident hero form, skip the
    // personal-details step and submit directly.
    if (hasContact) {
      setTimeout(() => doSubmit({ incidentDate: value }), 150);
    } else {
      setTimeout(() => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1)), 120);
    }
  };

  const continueLocation = () => {
    if (!answers.location.trim()) { setError('Please enter the city and state.'); return; }
    goNext();
  };

  const doSubmit = async (overrides = {}) => {
    const a = { ...answers, ...overrides };
    if (a.firstName.trim().length < 1 || a.lastName.trim().length < 1) { setError('Please enter your first and last name.'); return; }
    if (a.phone.replace(/\D/g, '').length < 10) { setError('Please enter a valid phone number.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) { setError('Please enter a valid email address so we can send your document.'); return; }
    if (!consent) { setError('Please agree to be contacted to continue.'); return; }
    setError('');
    setStatus('submitting');

    const description = [
      a.injuries && `Injuries: ${a.injuries}`,
      a.fault && `At fault: ${a.fault}`,
      a.how && `How it happened: ${a.how}`,
    ].filter(Boolean).join('\n');

    const fullName = `${a.firstName.trim()} ${a.lastName.trim()}`.trim();
    const payload = {
      name: fullName,
      phone: a.phone,
      email: a.email,
      location: a.location,
      incidentDate: a.incidentDate,
      description,
      injured: a.injured,
    };

    try {
      const res = await fetch(`${API_URL}/api/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        sendToLeadWebhook({
          source: 'get-started',
          first_name: a.firstName.trim(),
          last_name: a.lastName.trim(),
          full_name: fullName,
          phone: a.phone,
          email: a.email,
          location: a.location,
          incident_date: a.incidentDate,
          injured: a.injured,
          fault: a.fault,
          injuries: a.injuries,
          how: a.how,
          page_path: '/get-started',
        });
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'lead_form_submit',
          form_name: 'car_accident_get_started',
          page_path: '/get-started',
        });
        if (ENABLE_ESIGN) {
          try {
            const sres = await fetch(`${API_URL}/api/sign/embedded-url`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: fullName, email: a.email }),
            });
            const sdata = await sres.json();
            if (sdata.success && sdata.sign_url) {
              setSignData({ url: sdata.sign_url, clientId: sdata.client_id });
              setStatus('signing');
              openEmbedded(sdata.sign_url, sdata.client_id);
              return;
            }
          } catch { /* fall through to confirmation */ }
        }
        setStatus('confirmed');
      } else { setError(`We encountered an issue. Please call us directly at ${PHONE_DISPLAY}.`); setStatus('idle'); }
    } catch {
      setError(`We encountered an issue. Please call us directly at ${PHONE_DISPLAY}.`);
      setStatus('idle');
    }
  };

  const submit = (e) => { e.preventDefault(); doSubmit(); };

  if (status === 'confirmed') {
    const notAtFault = answers.fault && answers.fault !== 'Me';
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center" data-testid={`${idPrefix}confirmation-step`}>
        <div className="w-14 h-14 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-5">
          <Check className="h-7 w-7 text-green-600" strokeWidth={2.4} />
        </div>
        <h3 className="text-2xl font-extrabold tracking-tight text-slate-900 mb-3">Thank you — we've received your request.</h3>

        {notAtFault && (
          <div className="bg-yellow-400/15 border border-yellow-400/40 rounded-xl px-5 py-4 mb-5" data-testid={`${idPrefix}settlement-range`}>
            <p className="text-slate-900 text-[16px] font-bold leading-relaxed">
              Accidents like yours have settled for between $390K and $1.8M.
            </p>
          </div>
        )}

        <p className="text-slate-600 text-[15px] leading-relaxed mb-6">
          A team member is reviewing your information and will call you shortly. Prefer to talk now?
        </p>

        <a href={PHONE_LINK}
          className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl bg-yellow-400 text-[#0f1f4d] font-extrabold text-[17px] hover:bg-yellow-300 transition-colors uppercase tracking-wide shadow-md"
          data-testid={`${idPrefix}call-now-btn`}>
          <Phone className="h-5 w-5" /> Call Now {PHONE_DISPLAY}
        </a>

        <p className="text-slate-400 text-[11px] mt-5 leading-relaxed">
          Past results do not guarantee future outcomes. Submitting this form does not create an attorney-client
          relationship and does not guarantee case acceptance or compensation. Your matter must be reviewed and accepted by the firm.
        </p>
      </div>
    );
  }

  if (status === 'signing') {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center" data-testid={`${idPrefix}sign-step`}>
        <div className="w-14 h-14 rounded-full bg-yellow-400/20 flex items-center justify-center mx-auto mb-5">
          <FileText className="h-7 w-7 text-primary" />
        </div>
        <h3 className="text-2xl font-extrabold tracking-tight text-slate-900 mb-2">Last step: review and sign.</h3>
        <p className="text-slate-600 text-[15px] leading-relaxed mb-6">
          You're almost done. Please review the secure Dropbox Sign document to complete the next step.
        </p>
        <button type="button" onClick={() => signData && openEmbedded(signData.url, signData.clientId)}
          className="w-full py-4 rounded-xl bg-yellow-400 text-[#0f1f4d] font-extrabold text-[17px] hover:bg-yellow-300 transition-colors uppercase tracking-wide shadow-md"
          data-testid={`${idPrefix}open-sign-btn`}>
          Review &amp; Sign Document
        </button>
        <p className="text-slate-400 text-[11px] mt-5 leading-relaxed">
          Reviewing or signing this document does not create an attorney-client relationship and does not
          guarantee case acceptance or compensation. Your matter must be reviewed and accepted by the firm.
        </p>
      </div>
    );
  }

  const optionStep = step < QUIZ_STEPS.length ? QUIZ_STEPS[step] : null;

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-testid={`${idPrefix}lead-form`}>
      {/* Progress bar */}
      <div className="px-6 sm:px-7 pt-6">
        <div className="flex items-center justify-between mb-2">
          {step >= 1 ? (
            <button type="button" onClick={goBack} className="inline-flex items-center gap-1 text-slate-500 hover:text-primary text-sm font-semibold" data-testid={`${idPrefix}back-btn`}>
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
          ) : <span />}
          <span className="text-xs font-bold text-slate-400">{PROGRESS[step]}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden" data-testid={`${idPrefix}progress-bar`}>
          <div className="h-full bg-yellow-400 transition-all duration-300" style={{ width: `${PROGRESS[step]}%` }} />
        </div>
      </div>

      <div className="p-6 sm:p-7">
        {/* Option steps 1-4 */}
        {optionStep && (
          <div data-testid={`${idPrefix}step-${optionStep.key}`}>
            <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 mb-5 leading-snug">{optionStep.question}</h3>
            <div className="space-y-3">
              {optionStep.options.map((o) => {
                const selected = answers[optionStep.key] === o.label;
                return (
                  <button key={o.label} type="button" onClick={() => chooseOption(optionStep.key, o.label)}
                    className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 text-left transition-all ${selected ? 'border-yellow-400 bg-yellow-400/10' : 'border-slate-200 hover:border-primary/40 hover:bg-slate-50'}`}
                    data-testid={`${idPrefix}option-${optionStep.key}-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
                    <span className="flex-shrink-0 w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
                      <o.icon className="h-5 w-5 text-primary" strokeWidth={1.9} />
                    </span>
                    <span className="font-bold text-slate-900 text-[15px]">{o.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 5 — Location */}
        {step === 4 && (
          <div data-testid={`${idPrefix}step-location`}>
            <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 mb-5 leading-snug">Where did the accident happen?</h3>
            <Field label="City and State">
              <CityAutocomplete
                value={answers.location}
                onChange={(v) => { set('location', v); setError(''); }}
                idPrefix={idPrefix}
              />
            </Field>
            {error && <p className="text-red-600 text-sm mt-3" data-testid={`${idPrefix}form-error`}>{error}</p>}
            <button type="button" onClick={continueLocation}
              className="w-full mt-5 py-4 rounded-xl bg-yellow-400 text-[#0f1f4d] font-extrabold text-[17px] hover:bg-yellow-300 transition-colors uppercase tracking-wide shadow-md"
              data-testid={`${idPrefix}continue-btn`}>Continue</button>
          </div>
        )}

        {/* Step 6 — Date (option-based) */}
        {step === 5 && (
          <div data-testid={`${idPrefix}step-date`}>
            <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 mb-5 leading-snug">When did your accident happen?</h3>
            <div className="space-y-3">
              {DATE_OPTIONS.map((o) => {
                const selected = answers.incidentDate === o.label;
                return (
                  <button key={o.label} type="button" onClick={() => chooseDate(o.label)}
                    className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 text-left transition-all ${selected ? 'border-yellow-400 bg-yellow-400/10' : 'border-slate-200 hover:border-primary/40 hover:bg-slate-50'}`}
                    data-testid={`${idPrefix}option-date-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
                    <span className="flex-shrink-0 w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
                      <o.icon className="h-5 w-5 text-primary" strokeWidth={1.9} />
                    </span>
                    <span className="font-bold text-slate-900 text-[15px]">{o.label}</span>
                  </button>
                );
              })}
            </div>
            {error && <p className="text-red-600 text-sm mt-3" data-testid={`${idPrefix}form-error`}>{error}</p>}
          </div>
        )}

        {/* Step 7 — Personal details */}
        {step === 6 && (
          <form onSubmit={submit} data-testid={`${idPrefix}step-personal`}>
            <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 mb-1 leading-snug">Almost done — where should we reach you?</h3>
            <p className="text-slate-500 text-sm mb-5">Enter your details to complete your free case review request.</p>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First Name" required>
                  <input type="text" value={answers.firstName} onChange={upd('firstName')} placeholder="First name"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 text-[15px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                    data-testid={`${idPrefix}input-first-name`} />
                </Field>
                <Field label="Last Name" required>
                  <input type="text" value={answers.lastName} onChange={upd('lastName')} placeholder="Last name"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 text-[15px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                    data-testid={`${idPrefix}input-last-name`} />
                </Field>
              </div>
              <Field label="Phone Number" required>
                <input type="tel" value={answers.phone} onChange={upd('phone')} placeholder="(555) 555-5555"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 text-[15px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                  data-testid={`${idPrefix}input-phone`} />
              </Field>
              <Field label="Email Address" required>
                <input type="email" value={answers.email} onChange={upd('email')} placeholder="email@example.com"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 text-[15px] text-slate-800 placeholder-slate-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                  data-testid={`${idPrefix}input-email`} />
              </Field>

              <button type="submit" disabled={status === 'submitting'}
                className="w-full py-4 rounded-xl bg-yellow-400 text-[#0f1f4d] font-extrabold text-[18px] hover:bg-yellow-300 transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-md uppercase tracking-wide"
                data-testid={`${idPrefix}submit-btn`}>
                {status === 'submitting'
                  ? <><Loader2 className="h-5 w-5 animate-spin" /> Submitting...</>
                  : 'Request Free Case Review'}
              </button>

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); setError(''); }}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-primary flex-shrink-0"
                  data-testid={`${idPrefix}consent-checkbox`} />
                <span className="text-[12px] text-slate-500 leading-relaxed">{FORM_CONSENT}</span>
              </label>

              {error && <p className="text-red-600 text-sm" data-testid={`${idPrefix}form-error`}>{error}</p>}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const CarAccidentGetStarted = () => (
  <div className="min-h-screen flex flex-col font-body bg-gradient-to-b from-white to-slate-50" data-testid="get-started-page">
    <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
      <div className="max-w-[1180px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <img src={LOGO} alt="Rizzi Law Group" className="h-9 md:h-11 w-auto" style={{ imageRendering: '-webkit-optimize-contrast' }} />
        <a href={PHONE_LINK} className="flex items-center gap-2 text-primary font-extrabold text-[15px] md:text-[18px] hover:text-primary/80 transition-colors" data-testid="header-call-link">
          <Phone className="h-4 w-4 md:h-5 md:w-5" /> {PHONE_DISPLAY}
        </a>
      </div>
    </header>

    <main className="flex-1 px-4 py-10 sm:py-14">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-7">
          <p className="text-primary font-bold text-[13px] tracking-[0.15em] uppercase mb-3">Free Case Review</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mb-3 leading-tight">
            Let's get started
          </h1>
          <p className="text-slate-600 text-base">
            Answer a few quick questions about your accident. It only takes a couple of minutes — no obligation.
          </p>
        </div>
        <MultiStepForm idPrefix="hero-" />
        <p className="text-center text-slate-400 text-[12px] mt-6 leading-relaxed">
          Results may vary depending on your particular facts and legal circumstances.
        </p>
      </div>
    </main>
  </div>
);

export default CarAccidentGetStarted;
